
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { ChildCard } from "./ChildCard";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

interface ChildProfile {
  id: string;
  parent_id: string;
  name: string;
  age: number;
  progress: any;
  created_at: string;
  updated_at: string;
  profile_picture_url: string;
  birthday: string;
  grade_level?: string;
  badges?: string[];
}

export const ChildProfileList = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: children = [], isLoading, error } = useQuery<ChildProfile[]>({
    queryKey: ["childProfiles"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error("No authenticated session found");
        throw new Error("No authenticated session");
      }

      console.log("Fetching profiles for user ID:", session.user.id);

      const { data, error } = await supabase
        .from("child_profiles")
        .select("*")
        .eq('parent_id', session.user.id)
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error("Error fetching profiles:", error);
        throw error;
      }
      
      return data || [];
    },
  });

  useEffect(() => {
    const setupRealtimeSubscription = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Subscribe to changes in the child_profiles table for this user
      const subscription = supabase
        .channel('child_profiles_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'child_profiles',
            filter: `parent_id=eq.${session.user.id}`
          },
          (payload) => {
            console.log('Real-time update received:', payload);
            // Invalidate and refetch the query to get the latest data
            queryClient.invalidateQueries({ queryKey: ["childProfiles"] });
          }
        )
        .subscribe();

      // Return cleanup function
      return () => {
        subscription.unsubscribe();
      };
    };

    let cleanup: (() => void) | undefined;
    
    // IIFE to handle the async setup
    (async () => {
      cleanup = await setupRealtimeSubscription();
    })();

    // Cleanup function for useEffect
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [queryClient]);

  if (isLoading) {
    return <div className="text-center">Loading profiles...</div>;
  }

  if (error) {
    console.error("Error in component:", error);
    toast({
      title: "Error",
      description: "Failed to fetch child profiles",
      variant: "destructive",
    });
    return (
      <div className="text-center text-red-500">
        Failed to load profiles. Please try again.
      </div>
    );
  }

  if (!children || children.length === 0) {
    return (
      <div className="text-center text-gray-500">
        No child profiles found. Add your first child profile above.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {children.map((child) => (
        <ChildCard
          key={child.id}
          id={child.id}
          name={child.name}
          age={child.age}
          gradeLevel={child.grade_level || ''}
          badges={child.badges || []}
          profilePictureUrl={child.profile_picture_url}
          birthday={child.birthday}
        />
      ))}
    </div>
  );
};
